import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

interface TradingFormProps {
  currentPrice: number;
}

export default function TradingForm({ currentPrice }: TradingFormProps) {
  const navigate = useNavigate();
  const [tradeType, setTradeType] = useState<'buy' | 'sell'>('buy');
  const [orderType, setOrderType] = useState<'market' | 'limit'>('market');
  const [tradeAmount, setTradeAmount] = useState('');
  const [tradePrice, setTradePrice] = useState('');
  const [tradeTotal, setTradeTotal] = useState('');

  useEffect(() => {
    if (orderType === 'market') {
      if (tradeAmount) {
        const total = parseFloat(tradeAmount) * currentPrice;
        setTradeTotal(total.toFixed(2));
      } else {
        setTradeTotal('');
      }
    } else {
      if (tradeAmount && tradePrice) {
        const total = parseFloat(tradeAmount) * parseFloat(tradePrice);
        setTradeTotal(total.toFixed(2));
      } else {
        setTradeTotal('');
      }
    }
  }, [tradeAmount, tradePrice, currentPrice, orderType]);

  const handleTrade = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/auth/signin');
        return;
      }

      if (!tradeAmount || (orderType === 'limit' && !tradePrice)) {
        alert('Please fill in all required fields');
        return;
      }

      const amount = parseFloat(tradeAmount);
      const price = orderType === 'market' ? currentPrice : parseFloat(tradePrice);
      const total = parseFloat(tradeTotal);

      const { error } = await supabase
        .from('transactions')
        .insert({
          user_id: user.id,
          type: tradeType,
          amount: tradeType === 'buy' ? total : amount,
          status: 'completed',
          description: `${tradeType.toUpperCase()} ${amount} PEP at $${price.toFixed(4)}`
        });

      if (error) throw error;

      alert(`${tradeType.toUpperCase()} order placed successfully!`);
      setTradeAmount('');
      setTradePrice('');
      setTradeTotal('');
      
    } catch (error) {
      console.error('Error placing trade:', error);
      alert('Failed to place order. Please try again.');
    }
  };

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg sm:rounded-xl border border-gray-700 p-3 sm:p-4">
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setTradeType('buy')}
          className={`flex-1 py-2 rounded-lg font-medium text-sm transition-colors cursor-pointer whitespace-nowrap ${
            tradeType === 'buy'
              ? 'bg-green-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          Buy
        </button>
        <button
          onClick={() => setTradeType('sell')}
          className={`flex-1 py-2 rounded-lg font-medium text-sm transition-colors cursor-pointer whitespace-nowrap ${
            tradeType === 'sell'
              ? 'bg-red-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          Sell
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setOrderType('market')}
          className={`flex-1 py-1.5 rounded text-xs transition-colors cursor-pointer whitespace-nowrap ${
            orderType === 'market'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          Market
        </button>
        <button
          onClick={() => setOrderType('limit')}
          className={`flex-1 py-1.5 rounded text-xs transition-colors cursor-pointer whitespace-nowrap ${
            orderType === 'limit'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          Limit
        </button>
      </div>

      <div className="space-y-3">
        {orderType === 'limit' && (
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Price (USDT)</label>
            <input
              type="number"
              value={tradePrice}
              onChange={(e) => setTradePrice(e.target.value)}
              placeholder={currentPrice.toFixed(4)}
              className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg text-sm border border-gray-600 focus:border-blue-500 focus:outline-none"
            />
          </div>
        )}

        <div>
          <label className="text-xs text-gray-400 mb-1 block">Amount (PEP)</label>
          <input
            type="number"
            value={tradeAmount}
            onChange={(e) => setTradeAmount(e.target.value)}
            placeholder="0.00"
            className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg text-sm border border-gray-600 focus:border-blue-500 focus:outline-none"
          />
          <div className="flex gap-1 mt-2">
            {['25%', '50%', '75%', '100%'].map((percent) => (
              <button
                key={percent}
                onClick={() => setTradeAmount((10000 * parseInt(percent) / 100 / currentPrice).toFixed(2))}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-gray-300 py-1 rounded text-[10px] transition-colors cursor-pointer whitespace-nowrap"
              >
                {percent}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs text-gray-400 mb-1 block">Total (USDT)</label>
          <input
            type="text"
            value={tradeTotal}
            readOnly
            placeholder="0.00"
            className="w-full bg-gray-700/50 text-white px-3 py-2 rounded-lg text-sm border border-gray-600"
          />
        </div>

        <div className="bg-gray-700/30 rounded-lg p-2 text-xs">
          <div className="flex justify-between mb-1">
            <span className="text-gray-400">Available:</span>
            <span className="text-white">10,000 USDT</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Fee (0.1%):</span>
            <span className="text-white">{tradeTotal ? (parseFloat(tradeTotal) * 0.001).toFixed(2) : '0.00'} USDT</span>
          </div>
        </div>

        <button
          onClick={handleTrade}
          className={`w-full py-3 rounded-lg font-bold text-white transition-colors cursor-pointer whitespace-nowrap ${
            tradeType === 'buy'
              ? 'bg-green-600 hover:bg-green-700'
              : 'bg-red-600 hover:bg-red-700'
          }`}
        >
          {tradeType === 'buy' ? 'Buy' : 'Sell'} PEP
        </button>
      </div>
    </div>
  );
}
